import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home,
  Shield,
  FileText,
  AlertCircle,
  ClipboardList,
  CreditCard,
  User,
  Lock,
  Bell,
  HelpCircle,
  MessageSquare,
  FileCheck,
  LogOut,
  X,
  XCircle,
  ChevronRight,
  ExternalLink,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';

export default function PortalNavigation({ 
  isOpen, 
  onClose, 
  onLogout, 
  policyNumber,
  userName,
  claimsCount = 0
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount, isConnected } = useNotifications();

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore scroll position
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Handle internal navigation
  const handleNavigation = (path, isExternal = false) => {
    if (isExternal) {
      window.open(path, '_self');
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
    onClose();
  };

  // Check if menu item is active based on current route
  const isActive = (path, exact = false) => {
    // Don't highlight external links
    if (path.startsWith('http')) return false;
    
    // Handle query params - extract base path and query
    const [pathWithoutQuery, queryString] = path.split('?');
    const currentPath = location.pathname;
    const currentSearch = location.search;
    
    // For exact matches
    if (exact) {
      return currentPath === pathWithoutQuery;
    }
    
    // Special case for /portal (dashboard) - only match exactly
    if (pathWithoutQuery === '/portal') {
      return currentPath === '/portal';
    }
    
    // For /settings routes, match based on tab parameter
    if (pathWithoutQuery === '/settings') {
      if (currentPath !== '/settings') return false;
      // If menu item has a tab param, check if it matches current tab
      if (queryString) {
        const menuTab = new URLSearchParams(queryString).get('tab');
        const currentTab = new URLSearchParams(currentSearch).get('tab') || 'policy'; // default tab is 'policy'
        return menuTab === currentTab;
      }
      // If menu item is just /settings with no tab, only match if no tab in URL
      return !currentSearch || currentSearch === '?';
    }
    
    // For /documents and /cancel routes with policy number, match the base path
    if (pathWithoutQuery.startsWith('/documents/') || pathWithoutQuery.startsWith('/cancel/')) {
      return currentPath.startsWith(pathWithoutQuery.split('/').slice(0, 2).join('/') + '/');
    }
    
    // For other routes, exact path match only (no startsWith)
    return currentPath === pathWithoutQuery;
  };

  // Menu sections configuration - conditionally include Track a Claim
  const coreActions = [
    { 
      label: 'Dashboard', 
      icon: Home, 
      path: '/portal',
      description: 'Overview of your policy',
      exact: true
    },
    { 
      label: 'My Coverage', 
      icon: Shield, 
      path: '/coverage',
      description: 'View coverage details'
    },
    { 
      label: 'Documents', 
      icon: FileText, 
      path: policyNumber ? `/documents/${policyNumber}` : '/portal',
      description: 'Policy documents & forms'
    },
    { 
      label: 'File a Claim', 
      icon: AlertCircle, 
      path: '/claims/file',
      description: 'Start a new claim'
    },
    // Only show Track a Claim if user has filed claims
    ...(claimsCount > 0 ? [{ 
      label: 'Track a Claim', 
      icon: ClipboardList, 
      path: '/claims',
      description: `View ${claimsCount} existing claim${claimsCount > 1 ? 's' : ''}`
    }] : []),
  ];

  const policyBilling = [
    { 
      label: 'Payment & Billing', 
      icon: CreditCard, 
      path: '/settings?tab=billing',
      description: 'Manage payment methods'
    },
  ];

  const accountSettings = [
    { 
      label: 'Profile & Contact Info', 
      icon: User, 
      path: '/settings?tab=profile',
      description: 'Update your information'
    },
    { 
      label: 'Security Settings', 
      icon: Lock, 
      path: '/settings?tab=security',
      description: 'Password & login security'
    },
    { 
      label: 'Notification Preferences', 
      icon: Bell, 
      path: '/settings?tab=preferences',
      description: 'Manage alerts & emails'
    },
  ];

  const support = [
    { 
      label: 'Help Center / FAQ', 
      icon: HelpCircle, 
      path: '/help',
      description: 'Find answers'
    },
    { 
      label: 'Contact Support', 
      icon: MessageSquare, 
      path: '/contact',
      description: 'Get in touch'
    },
  ];

  const legal = [
    { label: 'State Notices', path: 'https://losspay.com/legal/state-notices', isExternal: true },
    { label: 'Privacy Policy', path: 'https://losspay.com/privacy', isExternal: true },
    { label: 'Terms of Service', path: 'https://losspay.com/terms', isExternal: true },
  ];

  const MenuItem = ({ item, compact = false }) => {
    const Icon = item.icon;
    const active = !item.isExternal && isActive(item.path, item.exact);
    const isDanger = item.isDanger;
    
    return (
      <button
        onClick={() => handleNavigation(item.path, item.isExternal)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${
          active 
            ? 'bg-[#3B5BDB] text-white' 
            : isDanger
              ? 'text-red-600 hover:bg-red-50'
              : 'text-slate-700 hover:bg-slate-50'
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            active 
              ? 'bg-white/20' 
              : isDanger
                ? 'bg-red-100'
                : 'bg-slate-100'
          }`}>
            <Icon className={`h-4 w-4 ${active ? 'text-white' : isDanger ? 'text-red-500' : 'text-slate-600'}`} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${active ? 'text-white' : isDanger ? 'text-red-600' : ''}`}>
            {item.label}
          </p>
        </div>
        {item.isExternal ? (
          <ExternalLink className={`h-4 w-4 flex-shrink-0 ${active ? 'text-white/60' : 'text-slate-400'}`} />
        ) : (
          <ChevronRight className={`h-4 w-4 flex-shrink-0 ${active ? 'text-white/60' : isDanger ? 'text-red-400' : 'text-slate-400'}`} />
        )}
      </button>
    );
  };

  const SectionDivider = ({ label }) => (
    <div className="px-3 pt-4 pb-1">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
        {label}
      </p>
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - Rendered at document root level */}
      <div 
        onClick={onClose}
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          touchAction: 'none',
          WebkitBackdropFilter: 'blur(4px)',
          backdropFilter: 'blur(4px)',
        }}
        className="md:hidden"
      />
      
      {/* Slide-in Menu - Full height with proper mobile viewport */}
      <div 
        className="md:hidden flex flex-col"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '85%',
          maxWidth: '384px',
          height: '100dvh',
          maxHeight: '100dvh',
          backgroundColor: 'white',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          zIndex: 9999,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms ease-out',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Header - Sticky */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <div>
            <p className="font-semibold text-slate-900">{userName || 'Menu'}</p>
            <p className="text-xs text-slate-500">Policyholder Portal</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-lg transition-colors"
            data-testid="nav-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Menu Content */}
        <div 
          className="flex-1 overflow-y-auto overscroll-contain"
          style={{ 
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div className="py-2">
            {/* Section 1: Core Actions */}
            <div className="px-2 space-y-0.5">
              {coreActions.map((item) => (
                <MenuItem key={item.label} item={item} />
              ))}
            </div>

            {/* Section 2: Policy & Billing */}
            <SectionDivider label="Policy & Billing" />
            <div className="px-2 space-y-0.5">
              {policyBilling.map((item) => (
                <MenuItem key={item.label} item={item} />
              ))}
            </div>

            {/* Section 3: Account Settings */}
            <SectionDivider label="Account" />
            <div className="px-2 space-y-0.5">
              {accountSettings.map((item) => (
                <MenuItem key={item.label} item={item} />
              ))}
            </div>

            {/* Section 4: Support */}
            <SectionDivider label="Support" />
            <div className="px-2 space-y-0.5">
              {support.map((item) => (
                <MenuItem key={item.label} item={item} />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Section - Sticky Footer */}
        <div 
          className="flex-shrink-0 border-t border-slate-200 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]"
          style={{
            paddingBottom: 'env(safe-area-inset-bottom, 8px)',
          }}
        >
          {/* Legal links */}
          <div className="px-4 pt-3 pb-2">
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
              {legal.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavigation(item.path, item.isExternal)}
                  className="text-xs text-slate-400 hover:text-slate-600 py-1"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Logout button - Large tap target */}
          <div className="px-3 pb-3">
            <button
              onClick={() => {
                onLogout();
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl font-semibold transition-colors active:scale-[0.98]"
              data-testid="nav-logout-btn"
            >
              <LogOut className="h-5 w-5" />
              Log Out
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
